import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, User as AuthUser } from '@/contexts/AuthContext';
import { useMenu } from '@/contexts/MenuContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { SessionTimeoutModal } from '@/components/auth/SessionTimeoutModal';
import { ContextProfileNudgeModal } from '@/components/profile/ContextProfileNudgeModal';
import { NotificationBell } from '@/components/layout/NotificationBell';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, ChevronRight, LogOut, User as UserIcon, Globe, Users } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { inquiryApi } from '@/api/inquiryApi';
import { teamApi } from '@/api/teamApi';
import { hasActiveTeam } from '@/lib/teamUtils';
import { trackEvent } from '@/lib/analytics';

const API_BASE_URL = `${BASE}/api`;

interface MenuItem {
  id: number;
  menuCode: string;
  menuName: string;
  menuPath: string | null;
  menuIcon: string | null;
  parentId: number | null;
  displayOrder: number;
  menuLevel: number;
  description: string | null;
  requiredRole: 'SUPER_ADMIN' | 'CLIENT';
  isActive: boolean;
  isExternal: boolean;
  createdAt: string;
  updatedAt: string | null;
  children: MenuItem[];
}

export const DashboardLayout: React.FC = () => {
  const { user, logout, showTimeoutWarning, remainingTime, extendSession,
    activeTeamId, myTeams, setActiveTeamId, setMyTeams } = useAuth();
  const { setMenuItems: setMenuContext } = useMenu();
  const location = useLocation();
  // 데스크탑에서는 사이드바 열림, 모바일에서는 닫힘
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  });
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [expandedMenus, setExpandedMenus] = useState<number[]>([]);
  const { language, setLanguage } = useLanguage();
  const [showProfileNudge, setShowProfileNudge] = useState(false);
  const [unreadInquiryCount, setUnreadInquiryCount] = useState(0);
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 1024 : false;
  });

  // 로그인 사용자의 저장된 언어 선호도를 공용 language 컨텍스트에 최초 1회만 동기화한다.
  // (이후 사용자가 토글을 직접 눌러 바꾼 값을 user 객체 재조회 등으로 다시 덮어쓰지 않기 위함)
  const hasSyncedPreferredLanguageRef = useRef(false);
  useEffect(() => {
    if (!hasSyncedPreferredLanguageRef.current && user?.preferredLanguage) {
      setLanguage(user.preferredLanguage);
      hasSyncedPreferredLanguageRef.current = true;
    }
  }, [user?.preferredLanguage, setLanguage]);

  // 메뉴 로드 (language 변경 시)
  useEffect(() => {
    loadMenuItems();
  }, [language]);

  // 미답변 문의 수 로드
  const { data: unreadCountData } = useQuery({
    queryKey: ['unreadInquiryCount'],
    queryFn: () => inquiryApi.getUnreadCount(),
    enabled: !!user,
  });

  // 미답변 문의 수 상태 업데이트
  useEffect(() => {
    if (unreadCountData) {
      setUnreadInquiryCount((unreadCountData.data as any)?.count ?? 0);
    }
  }, [unreadCountData]);

  // 내가 속한 팀 목록 로드
  const { data: teamsData } = useQuery({
    queryKey: ['myTeams', user?.userRole],
    queryFn: () => teamApi.getMyTeams(),
    enabled: !!user,
  });

  // 팀 목록 상태 업데이트
  useEffect(() => {
    if (teamsData?.data) {
      // axios 인터셉터 변환 반영: res.data가 이미 변환된 형태
      const data = Array.isArray(teamsData.data)
        ? teamsData.data
        : (teamsData.data as any)?.content ?? [];
      setMyTeams(data);
    }
  }, [teamsData]);

  // 최초 로그인 후 컨텍스트 프로필 미등록 유도 모달
  const { data: profileStatusData } = useQuery({
    queryKey: ['altProfileStatus'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      return axios.get(`${API_BASE_URL}/user/alt-profiles/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },
    enabled: !!user,
  });

  // 프로필 상태에 따른 모달 표시
  useEffect(() => {
    if (profileStatusData?.data && !profileStatusData.data.hasProfile) {
      setShowProfileNudge(true);
    }
  }, [profileStatusData]);

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobileView(mobile);
      // 데스크탑으로 변경될 때 사이드바 자동 열기
      if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // 팀 컨텍스트(팀원)일 때 마이 요금제/청구/증빙설정 메뉴 숨김
  const isTeamContext = !!activeTeamId && myTeams.find(t => t.teamId === activeTeamId && t.myRole !== 'OWNER');
  const buildDefaultMenuItems = (role: AuthUser['userRole'] | undefined): MenuItem[] => {
    const now = new Date().toISOString();
    let baseMenus: MenuItem[] = [
      {
        id: 1,
        menuCode: 'DASHBOARD',
        menuName: '대시보드',
        menuPath: '/dashboard',
        menuIcon: '📊',
        parentId: null,
        displayOrder: 1,
        menuLevel: 0,
        description: '대시보드 페이지',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [],
      },
      {
        id: 10,
        menuCode: 'NOTICE',
        menuName: '공지사항',
        menuPath: '/dashboard/notices',
        menuIcon: '📢',
        parentId: null,
        displayOrder: 10,
        menuLevel: 0,
        description: '공지사항',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [],
      },
      {
        id: 11,
        menuCode: 'INQUIRY',
        menuName: '1:1 문의',
        menuPath: '/dashboard/inquiry',
        menuIcon: '💬',
        parentId: null,
        displayOrder: 11,
        menuLevel: 0,
        description: '1:1 문의하기',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [],
      },
      {
        id: 5,
        menuCode: 'API_GROUP',
        menuName: '이미지 API',
        menuPath: null,
        menuIcon: '🔗',
        parentId: null,
        displayOrder: 99,
        menuLevel: 0,
        description: 'API 연동 메뉴 그룹',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [
          {
            id: 51,
            menuCode: 'API_DOCS',
            menuName: 'API 연동 안내',
            menuPath: '/dashboard/api-docs',
            menuIcon: '📄',
            parentId: 5,
            displayOrder: 1,
            menuLevel: 1,
            description: 'Swagger 문서 및 연동 샘플',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 52,
            menuCode: 'API_KEY_MGMT',
            menuName: 'API키 관리',
            menuPath: '/dashboard/ApiKeyManagement',
            menuIcon: '🔑',
            parentId: 5,
            displayOrder: 2,
            menuLevel: 1,
            description: 'API Key 발급 및 관리',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 53,
            menuCode: 'EXTENSION_GUIDE',
            menuName: '크롬 확장 프로그램',
            menuPath: '/dashboard/extension-guide',
            menuIcon: '🧩',
            parentId: 5,
            displayOrder: 3,
            menuLevel: 1,
            description: 'Chrome 확장 프로그램 사용 가이드',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
        ],
      },
      {
        id: 2,
        menuCode: 'CRAWLER',
        menuName: 'ALT 생성',
        menuPath: null,
        menuIcon: '🌐',
        parentId: null,
        displayOrder: 2,
        menuLevel: 0,
        description: 'ALT 텍스트 생성 메뉴',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [
          {
            id: 21,
            menuCode: 'CRAWLER_WEBSITE',
            menuName: '웹사이트 크롤링',
            menuPath: '/dashboard/crawler/website',
            menuIcon: '🕷️',
            parentId: 2,
            displayOrder: 1,
            menuLevel: 1,
            description: '웹사이트 크롤링 페이지',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 22,
            menuCode: 'CRAWLER_IMAGE_URL',
            menuName: '이미지 URL',
            menuPath: '/dashboard/crawler/image-url',
            menuIcon: '🖼️',
            parentId: 2,
            displayOrder: 2,
            menuLevel: 1,
            description: '이미지 URL 입력 페이지',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 23,
            menuCode: 'CRAWLER_UPLOAD',
            menuName: '이미지 업로드',
            menuPath: '/dashboard/crawler/upload',
            menuIcon: '📤',
            parentId: 2,
            displayOrder: 3,
            menuLevel: 1,
            description: '이미지 업로드 페이지',
            requiredRole: 'CLIENT',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
        ],
      },
      {
        id: 3,
        menuCode: 'PROFILE',
        menuName: '프로필',
        menuPath: null,
        menuIcon: '👤',
        parentId: null,
        displayOrder: 3,
        menuLevel: 0,
        description: '프로필 관리 메뉴',
        requiredRole: 'CLIENT',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: (
          !isTeamContext
            ? ([
                {
                  id: 31,
                  menuCode: 'PROFILE_EDIT',
                  menuName: '프로필 수정',
                  menuPath: '/dashboard/profile/edit',
                  menuIcon: '✏️',
                  parentId: 3,
                  displayOrder: 1,
                  menuLevel: 1,
                  description: '프로필 수정 페이지',
                  requiredRole: 'CLIENT',
                  isActive: true,
                  isExternal: false,
                  createdAt: now,
                  updatedAt: null,
                  children: [],
                },
                {
                  id: 32,
                  menuCode: 'PROFILE_WITHDRAW',
                  menuName: '회원 탈퇴',
                  menuPath: '/dashboard/profile/withdraw',
                  menuIcon: '🚪',
                  parentId: 3,
                  displayOrder: 2,
                  menuLevel: 1,
                  description: '회원 탈퇴 페이지',
                  requiredRole: 'CLIENT',
                  isActive: true,
                  isExternal: false,
                  createdAt: now,
                  updatedAt: null,
                  children: [],
                },
                {
                  id: 33,
                  menuCode: 'PROFILE_CONTEXT',
                  menuName: '컨텍스트 프로필',
                  menuPath: '/dashboard/profile/context',
                  menuIcon: '🎯',
                  parentId: 3,
                  displayOrder: 3,
                  menuLevel: 1,
                  description: 'AI ALT 텍스트 컨텍스트 프로필 설정',
                  requiredRole: 'CLIENT',
                  isActive: true,
                  isExternal: false,
                  createdAt: now,
                  updatedAt: null,
                  children: [],
                },
              ] as MenuItem[])
            : []
        ),
      },
    ];

    if (role === 'SUPER_ADMIN') {
      baseMenus.push({
        id: 4,
        menuCode: 'ADMIN',
        menuName: '관리자',
        menuPath: null,
        menuIcon: '⚙️',
        parentId: null,
        displayOrder: 4,
        menuLevel: 0,
        description: '관리자 메뉴',
        requiredRole: 'SUPER_ADMIN',
        isActive: true,
        isExternal: false,
        createdAt: now,
        updatedAt: null,
        children: [
          {
            id: 41,
            menuCode: 'ADMIN_USERS',
            menuName: '사용자 관리',
            menuPath: '/dashboard/admin/users',
            menuIcon: '👥',
            parentId: 4,
            displayOrder: 1,
            menuLevel: 1,
            description: '사용자 관리 페이지',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 42,
            menuCode: 'ADMIN_MENU',
            menuName: '메뉴 구조 관리',
            menuPath: '/dashboard/admin/menu',
            menuIcon: '📋',
            parentId: 4,
            displayOrder: 2,
            menuLevel: 1,
            description: '메뉴 구조 관리 페이지',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 43,
            menuCode: 'ADMIN_PAYMENTS',
            menuName: '결제 관리',
            menuPath: '/dashboard/admin/payments',
            menuIcon: '💳',
            parentId: 4,
            displayOrder: 3,
            menuLevel: 1,
            description: '결제 목록/확인/환불 및 Workspace 정기결제(신규) 관리',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 44,
            menuCode: 'ADMIN_MEMBER_BILLING_STATUS',
            menuName: '유료회원 결제 현황',
            menuPath: '/dashboard/admin/member-billing-status',
            menuIcon: '🧾',
            parentId: 4,
            displayOrder: 4,
            menuLevel: 1,
            description: '유료 플랜 워크스페이스별 이번 달 결제기록/상태를 한눈에 모니터링',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 46,
            menuCode: 'ADMIN_ENTERPRISE_CONTRACT_STATUS',
            menuName: '엔터프라이즈 계약 현황',
            menuPath: '/dashboard/admin/enterprise-contract-status',
            menuIcon: '🏢',
            parentId: 4,
            displayOrder: 9,
            menuLevel: 1,
            description: '개인/팀 엔터프라이즈 계약 상태와 Workspace 상태 정합성을 모니터링',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 45,
            menuCode: 'ADMIN_CONTEXT_ITEMS',
            menuName: '공통코드 관리',
            menuPath: '/dashboard/admin/alt-context-items',
            menuIcon: '🏷️',
            parentId: 4,
            displayOrder: 5,
            menuLevel: 1,
            description: '공통코드(컨텍스트 옵션) 관리',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 46,
            menuCode: 'ADMIN_NOTICES',
            menuName: '공지사항 관리',
            menuPath: '/dashboard/admin/notices',
            menuIcon: '📢',
            parentId: 4,
            displayOrder: 6,
            menuLevel: 1,
            description: '공지사항 관리',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 47,
            menuCode: 'ADMIN_INQUIRIES',
            menuName: '1:1 문의 관리',
            menuPath: '/dashboard/admin/inquiries',
            menuIcon: '💬',
            parentId: 4,
            displayOrder: 7,
            menuLevel: 1,
            description: '문의 관리',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
          {
            id: 54,
            menuCode: 'ADMIN_TRIAL_MONITORING',
            menuName: '무료체험/진단 모니터링',
            menuPath: '/dashboard/admin/trial-monitoring',
            menuIcon: '📈',
            parentId: 4,
            displayOrder: 8,
            menuLevel: 1,
            description: '비회원 무료체험/무료진단 퍼널 통계 및 일일 상한 조정',
            requiredRole: 'SUPER_ADMIN',
            isActive: true,
            isExternal: false,
            createdAt: now,
            updatedAt: null,
            children: [],
          },
        ],
      });
    }

    return baseMenus;
  };

  const filterMenusByRole = (items: MenuItem[], role?: AuthUser['userRole']): MenuItem[] => {
    const canAccess = (requiredRole: MenuItem['requiredRole']) => {
      if (requiredRole === 'CLIENT') return true;
      if (!role) return false;
      return role === 'SUPER_ADMIN';
    };

    return items
      .filter(item => item.isActive && canAccess(item.requiredRole))
      .map(item => ({
        ...item,
        children: item.children ? filterMenusByRole(item.children, role) : [],
      }));
  };

  // 백엔드 menu_path를 프론트엔드 라우팅 경로로 변환
  const transformMenuPaths = (items: MenuItem[]): MenuItem[] => {
    return items.map(item => {
      const transformedItem = { ...item };
      
      // menuPath가 있고 /dashboard로 시작하지 않는 경우 prefix 추가
      if (transformedItem.menuPath && !transformedItem.menuPath.startsWith('/dashboard')) {
        transformedItem.menuPath = `/dashboard${transformedItem.menuPath}`;
      }
      
      // 자식 메뉴도 재귀적으로 변환
      if (transformedItem.children && transformedItem.children.length > 0) {
        transformedItem.children = transformMenuPaths(transformedItem.children);
      }
      
      return transformedItem;
    });
  };

  const loadMenuItems = async () => {
    try {
      const userRole = user?.userRole || 'CLIENT';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      const response = await axios.get(`${API_BASE_URL}/menus?role=${userRole}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (response.data.success) {
        const incoming = response.data.data || [];
        
        // 디버깅: 백엔드에서 받은 메뉴 데이터 확인
        if (process.env.NODE_ENV === 'development') {
          console.log('Loaded menus from backend:', incoming);
        }
        
        // 백엔드 메뉴 데이터를 /dashboard prefix 추가하여 변환
        const transformedMenus = transformMenuPaths(incoming);
        const resolvedMenus = transformedMenus.length > 0
          ? filterMenusByRole(transformedMenus, user?.userRole)
          : buildDefaultMenuItems(user?.userRole);

        if (process.env.NODE_ENV === 'development') {
          console.log('Transformed menus:', resolvedMenus);
        }

        setMenuItems(resolvedMenus);
        setMenuContext(resolvedMenus); // 메뉴 Context에도 설정

        // 현재 경로에 해당하는 메뉴 자동 확장
        autoExpandCurrentMenu(resolvedMenus);
      } else {
        const fallbackMenus = buildDefaultMenuItems(user?.userRole);
        setMenuItems(fallbackMenus);
        setMenuContext(fallbackMenus); // 메뉴 Context에도 설정
        autoExpandCurrentMenu(fallbackMenus);
      }
    } catch (error: any) {
      console.warn('Failed to load menu items from server, using default menus:', error.message);
      
      // 디버깅: 폴백 메뉴 사용 알림
      if (process.env.NODE_ENV === 'development') {
        console.log('Using fallback (default) menus');
      }
      
      // API가 실패하면 기본 메뉴 사용
      const fallbackMenus = buildDefaultMenuItems(user?.userRole);
      setMenuItems(fallbackMenus);
      setMenuContext(fallbackMenus); // 메뉴 Context에도 설정
      autoExpandCurrentMenu(fallbackMenus);
    }
  };

  const autoExpandCurrentMenu = (items: MenuItem[]) => {
    const expanded: number[] = [];
    const currentPath = `${location.pathname}${location.hash}`;
    const findAndExpand = (items: MenuItem[], parentId: number | null = null) => {
      items.forEach(item => {
        if (item.menuPath === currentPath && parentId !== null) {
          expanded.push(parentId);
        }
        if (item.children && item.children.length > 0) {
          findAndExpand(item.children, item.id);
        }
      });
    };
    findAndExpand(items);
    setExpandedMenus(expanded);
  };

  const toggleMenu = (menuId: number) => {
    setExpandedMenus(prev =>
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const handleLogout = async () => {
    if (window.confirm('로그아웃하시겠습니까?')) {
      await logout();
      window.location.replace('/login');
    }
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    // menuPath에 해시가 포함된 메뉴(예: /dashboard/profile/edit#team)는
    // location.pathname만으로는 절대 일치할 수 없음 (hash는 pathname에 안 들어감)
    const isActive = item.menuPath === `${location.pathname}${location.hash}`;

    // 레벨별 padding 계산
    const getPaddingClass = (level: number) => {
      switch (level) {
        case 0: return 'px-4';
        case 1: return 'pl-8 pr-4';
        case 2: return 'pl-12 pr-4';
        case 3: return 'pl-16 pr-4';
        default: return `pl-${(level + 1) * 4} pr-4`;
      }
    };

    // 디버깅용 로그
    if (process.env.NODE_ENV === 'development') {
      console.log('Menu Item:', {
        name: item.menuName,
        path: item.menuPath,
        hasChildren,
        isActive,
      });
    }

    if (hasChildren) {
      return (
        <div key={item.id}>
          <button
            onClick={() => toggleMenu(item.id)}
            className={`w-full flex items-center justify-between py-3 text-left hover:bg-gray-100 transition-colors ${getPaddingClass(level)}`}
          >
            <span className="flex items-center gap-2">
              {item.menuIcon && <span>{item.menuIcon}</span>}
              <span className="font-medium truncate" title={item.menuName}>{item.menuName}</span>
            </span>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {isExpanded && (
            <div className="bg-gray-50 relative">
              {/* Tree 연결선 */}
              {level > 0 && (
                <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-300" />
              )}
              {item.children.map((child, index) => (
                <div key={child.id} className="relative">
                  {/* 수평 연결선 */}
                  {level > 0 && (
                    <div className="absolute left-4 top-3 w-4 h-px bg-gray-300" />
                  )}
                  {renderMenuItem(child, level + 1)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (item.isExternal && item.menuPath) {
      return (
        <a
          key={item.id}
          href={item.menuPath}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent('menu_click', { menu_code: item.menuCode, menu_name: item.menuName, menu_path: item.menuPath, is_external: 'true' });
            if (isMobileView) setSidebarOpen(false);
          }}
          className={`block py-3 hover:bg-gray-100 transition-colors ${getPaddingClass(level)}`}
        >
          <span className="flex items-center gap-2">
            {item.menuIcon && <span>{item.menuIcon}</span>}
            <span className="truncate" title={item.menuName}>{item.menuName}</span>
          </span>
        </a>
      );
    }

    // menuPath가 없으면 비활성화된 버튼으로 렌더링
    if (!item.menuPath || item.menuPath.trim() === '') {
      return (
        <button
          key={item.id}
          disabled
          className={`block w-full text-left py-3 opacity-50 cursor-not-allowed ${getPaddingClass(level)}`}
          title="경로가 설정되지 않았습니다"
        >
          <span className="flex items-center gap-2">
            {item.menuIcon && <span>{item.menuIcon}</span>}
            <span className="truncate" title={item.menuName}>{item.menuName}</span>
          </span>
        </button>
      );
    }

    const isInquiryMenu = item.menuPath === '/dashboard/inquiry';

    return (
      <Link
        key={item.id}
        to={item.menuPath}
        onClick={() => {
          trackEvent('menu_click', { menu_code: item.menuCode, menu_name: item.menuName, menu_path: item.menuPath, is_external: 'false' });
          if (isMobileView) setSidebarOpen(false);
        }}
        className={`block py-3 hover:bg-gray-100 transition-colors ${getPaddingClass(level)} ${isActive ? 'bg-primary text-white hover:bg-primary' : ''}`}
      >
        <span className="flex items-center gap-2">
          {item.menuIcon && <span>{item.menuIcon}</span>}
          <span className="truncate" title={item.menuName}>{item.menuName}</span>
          {isInquiryMenu && unreadInquiryCount > 0 && (
            <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">
              {unreadInquiryCount > 99 ? '99+' : unreadInquiryCount}
            </span>
          )}
        </span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 h-16">
          {/* Left: Logo & Menu Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white font-bold">
                A
              </div>
              <span className="font-bold text-xl hidden md:block">ALT Generation</span>
            </div>
          </div>

          {/* Right: User Info & Actions */}
          <div className="flex items-center gap-4">
            {/* Team Context Display */}
            {hasActiveTeam(myTeams) && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white border border-primary">
                <Users className="w-4 h-4" />
                <span className="hidden md:inline text-sm max-w-[120px] truncate">
                  {myTeams.find(t => activeTeamId ? t.teamId === activeTeamId : true)?.teamName ?? '팀'}
                </span>
              </div>
            )}

            {/* Language Switcher */}
            <button
              onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
              className="hidden flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="언어 변경"
            >
              <Globe className="w-5 h-5" />
              <span className="hidden md:inline">{language === 'ko' ? '한국어' : 'English'}</span>
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* User Info - 클릭 시 회원정보수정(프로필 수정) 페이지로 이동 */}
            <Link
              to="/dashboard/profile/edit"
              className="flex items-center gap-3 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              title="프로필 수정"
            >
              <UserIcon className="w-5 h-5" />
              <div className="hidden md:block">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-gray-600">{user?.userId}</p>
              </div>
            </Link>

            {/* Logout Button */}
            <Button onClick={handleLogout} variant="outline" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden md:inline">로그아웃</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden transition-transform duration-300 z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* 팀 컨텍스트 사이드바 표시 */}
        {activeTeamId && (
          <div className="mx-3 mt-3 mb-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span className="text-xs font-semibold text-blue-800 truncate">
                {myTeams.find(t => t.teamId === activeTeamId)?.teamName ?? '팀'}
              </span>
            </div>
            <p className="text-xs text-blue-700 mt-0.5">팀 컨텍스트로 작업 중</p>
          </div>
        )}
        <nav className="py-4">
          {menuItems.map(item => renderMenuItem(item))}
        </nav>
      </aside>

      {/* Main Content */}
      <main
        className={`pt-16 min-h-screen bg-gray-50 transition-all duration-300 overflow-x-hidden ${
          sidebarOpen ? 'lg:pl-64' : 'lg:pl-0'
        }`}
      >
        {/* 팀 컨텍스트 배너 */}
        {activeTeamId && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 md:px-8 py-2 flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 shrink-0 text-blue-600" />
            <span className="font-semibold text-blue-800">
              {myTeams.find(t => t.teamId === activeTeamId)?.teamName ?? '팀'}
            </span>
            <span className="text-blue-700">팀 컨텍스트로 작업 중입니다.</span>
            <button
              onClick={() => setActiveTeamId(null)}
              className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline transition-colors"
            >
              개인으로 전환
            </button>
          </div>
        )}
        <div className="pb-20">
          <Outlet />
        </div>
        
        {/* Dashboard Footer */}
        <footer 
          className={`bg-white border-t border-gray-200 py-4 px-4 md:px-8 transition-all duration-300 ${
            sidebarOpen ? 'lg:pl-64' : 'lg:pl-0'
          }`}
        >
          <div className="text-center text-sm text-gray-600">
            <p>© 2026 ALT Generation. All rights reserved.</p>
          </div>
        </footer>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && isMobileView && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Session Timeout Warning */}
      <SessionTimeoutModal
        isOpen={showTimeoutWarning}
        remainingSeconds={remainingTime}
        onExtend={extendSession}
        onLogout={handleLogout}
      />

      {/* 최초 로그인 컨텍스트 프로필 미등록 유도 모달 */}
      {showProfileNudge && (
        <ContextProfileNudgeModal onClose={() => setShowProfileNudge(false)} />
      )}
    </div>
  );
};
